import { ChangeDetectorRef, Component, inject, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProgramService } from '../../services/program.service';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Program } from '../../models/program';
import { ExpenseService } from '../../services/expense.service';
import { Expense } from '../../models/expense';
import { Residency } from '../../models/residency';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsCoreOption } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { TableData } from '../../models/table-data';
import { ModalDismissReasons, NgbModal, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { FeedbackComponent } from '../feedback/feedback.component';



echarts.use([TitleComponent, BarChart, GridComponent, CanvasRenderer, LineChart, TooltipComponent]);

const groupBy = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);
    if (!previous[group]) previous[group] = [];
    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);

@Component({
    selector: 'app-tuition-form',
    imports: [FormsModule, ReactiveFormsModule, CommonModule, NgxEchartsDirective, NgbTooltipModule, FeedbackComponent],
    providers: [ProgramService, ExpenseService, provideEchartsCore({ echarts })],
    templateUrl: './tuition-form.component.html',
    styleUrl: './tuition-form.component.scss'
})
export class TuitionFormComponent {
  public tuitionForm: FormGroup;
  public colleges$: Observable<String[]>;
  public programs$: Observable<String[]>;
  public selectedStudies?: string = "";
  public selectedResidency?: string = "";
  public selectedCollege?: string = "";
  public selectedProgramName?: string = "";
  public selectedProgram: Program = <Program>{};
  public isInternational = false;
  public studyTypes = [
    "Undergraduate",
    "Graduate"
  ];
  public residencyTypes: Array<Residency> = [
    { name: "Canadian - In-province", value: "In Province" },
    { name: "Canadian - Out-of-province", value: "Out of Province" },
    { name: "International", value: "International" }
  ];

  public livingForm: FormGroup;
  public housings$: Observable<Array<string | undefined>>;
  public mealPlans$: Observable<Array<string>>;
  public transportationOptions$: Observable<Array<string>>;
  public selectedHousing?: string = "";
  public housingOptions: Array<Expense> = [];
  public selectedHousingOption: Expense = <Expense>{};
  public selectedHasMealPlan?: string = "";
  public mealPlanOptions: Array<Expense> = [];
  public selectedMealPlan?: string = "";
  public selectedMealPlanOption: Expense = <Expense>{};
  public selectedTransportation?: string = "";
  public selectedTransportationOption: Expense = <Expense>{};
  public canPickMealPlanOption: boolean = false;
  public housingMealPlanInformation?: string = "";
  public selectedFees: Array<Expense> = [];
  public calledSavePDFAlready = false;

  public tableSelectedFees: Map<string, Expense[]> = new Map<string, Expense[]>;

  private otherFeesDefault:string = "Other fees collected for third parties";
  public assessmentTypeOrder: string[] = [
    "Assessed by USask", 
    "Assessed by USSU", 
    "Assessed by GSA", 
    this.otherFeesDefault
  ];


  public mealPlanOptionRepsonses = [
    "Yes",
    "No"
  ];

  private noMealPlan: Expense = {
    type: "Meal Plan",
    expense: "No Plan",
    eligibility: "All",
    cost: 0,
    subcategory: "",
    information: "",
    order: 0
  }

  public savingsForm: FormGroup;


  /**
   * Charting data
   */
  public chartOption: EChartsCoreOption = {
    title: {
      text: 'Expenses',
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        var tar = params[0];
        return tar.seriesName + ' : ' + `${this.formatMoney(tar.value)}`;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel'
    },
    xAxis: {
      type: 'category',
      data: ['Expenses', 'Earnings and Savings'],
      axisLabel: {
        interval: "0",
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: any) => {
          return `${this.formatMoney(value)}`;
        }
      }
    },
    series: [
      {
        name: "Value",
        label: {
          show: true,
          position: 'inside',
          formatter: (params: any) => {
            return this.formatMoney(params.value);
          },
        },
        data: [],
        type: 'bar',
        itemStyle: {
          borderColor: 'transparent',
          color: 'transparent'
        }
      },
    ],
    graph: {
      color: 'colorPalette'
    }
  };

  public chartLoading: boolean = false;
  public mergeChartData: any;

  public showResults: boolean = false;
  public totalTuition: number = 0;
  public totalFees: number = 0;
  public totalLiving: number = 0;
  public totalSavings: number = 0;
  public totalExpenses: number = 0;
  public grandTotal: number = 0;
  public totalSavingsArray: Array<TableData> = [];
  public totalTuitionArray: Array<TableData> = [];
  public totalLivingArray: Array<TableData> = [];
  public totalFeesArray: Array<TableData> = [];
  public graphData: Array<any> = [];
  public allExpenses: Array<Expense> = [];

  public transportationValue?: string;
  public mealPlanValue?: string;
  public housingValue?: string;
  public foodValue?: string;
  public incidentalsValue?: string;

  private modalReference: NgbModalRef;
  private modalService = inject(NgbModal);
  public closeResult = '';

  private maxTuitionCourses: number = 5;
  private fullCourseCountTotal: number = 10

  // What is considered the part time ceiling number before it is called full time in Fee terms
  private partTimeCourseMax: number = 2;

  public tuitionCourseNumbers: Array<number> = [];
  public selectedCourseLoadNumber?: number = undefined;
  

  constructor(
    private programService: ProgramService,
    private expenseService: ExpenseService,
    private ref: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.createTuitionCourseLoadArray();
    this.tuitionForm = new FormGroup({
      tuitionResidency: new FormControl(undefined, [Validators.required]),
      tuitionStudies: new FormControl(undefined, [Validators.required]),
      tuitionCollege: new FormControl(undefined, [Validators.required]),
      tuitionProgram: new FormControl(undefined, [Validators.required]),
      tuitionCourseLoad: new FormControl(undefined)
    });

    this.livingForm = new FormGroup({
      livingHousing: new FormControl(undefined, [Validators.required]),
      livingHousingOption: new FormControl(undefined),
      livingMealPlan: new FormControl(undefined),
      livingMealPlanOption: new FormControl(undefined),
      livingTransportation: new FormControl(undefined, [Validators.required])
    });

    this.savingsForm = new FormGroup({
      savingsGeneral: new FormControl(undefined),
      savingsWork: new FormControl(undefined),
      savingsStudentLoan: new FormControl(undefined),
      savingsAwardsAssistance: new FormControl(undefined),
      savingsFinancialSupportFamily: new FormControl(undefined),
      savingsOther: new FormControl(undefined)
    });

    this.onSubmit();

  }

  private createTuitionCourseLoadArray(){
    for(let i: number = 1; i <= this.maxTuitionCourses; i++){
      this.tuitionCourseNumbers.push(i);
    }
  }

  public loadExpenses() {
    this.clearHousing();
    this.clearMealPlan();
    this.clearTransportation();
    this.clearMealPlanInformation();
    this.clearExpenseValues();
    this.clearIncidentals();
    this.clearFood();

    this.livingForm.controls["livingHousingOption"].clearValidators();
    this.livingForm.controls["livingMealPlan"].clearValidators();
    this.livingForm.controls["livingMealPlanOption"].clearValidators();
    this.livingForm.controls["livingHousingOption"].updateValueAndValidity();
    this.livingForm.controls["livingMealPlan"].updateValueAndValidity();
    this.livingForm.controls["livingMealPlanOption"].updateValueAndValidity();


    this.housings$ = this.expenseService.getHousing(this.selectedStudies);
    this.mealPlans$ = this.expenseService.getMealPlans(this.selectedStudies);
    this.transportationOptions$ = this.expenseService.getTransportationOptions(this.selectedStudies);
    this.selectedFees = [];
    this.tableSelectedFees = new Map<string, Expense[]>;
    this.expenseService.getFeesCourseLoad(this.isFullTime(), this.selectedStudies).subscribe((fees) => {
      this.selectedFees = fees;
      let tempRecord:Record<string, Expense[]> =  groupBy(this.selectedFees, i => i.subcategory ?? this.otherFeesDefault);
      this.tableSelectedFees = new Map<string, Expense[]>(Object.entries(tempRecord));
      this.orderFeeKeys();
      this.onSubmit();
    });
  }

  public selectResidency() {
    this.selectedResidency = this.tuitionResidency?.value;
    if (this.selectedResidency == "International") {
      this.isInternational = true;
    } else {
      this.isInternational = false;
    }
    this.selectStudy();
  }

  public selectStudy() {
    this.selectedStudies = this.tuitionStudies?.value;
    this.clearCollege();
    this.clearProgram();
    if (this.tuitionResidency?.valid && this.tuitionStudies?.valid) {
      this.colleges$ = this.programService.getStudies(this.selectedStudies, this.selectedResidency);
    }
  }

  public selectCollege() {
    this.selectedCollege = this.tuitionCollege?.value;
    this.clearProgram();
    this.programs$ = this.programService.getPrograms(this.selectedStudies, this.selectedCollege, this.selectedResidency);
  }

  public selectProgram() {
    this.selectedProgramName = this.tuitionProgram?.value;
    this.programService.getSelectedProgram(this.selectedStudies, this.selectedCollege, this.selectedResidency, this.selectedProgramName).subscribe(program => {
      this.selectedProgram = program;
      this.clearCourseLoad();
      if(this.selectedProgram.modifyCourseLoad){
        this.tuitionForm.controls["tuitionCourseLoad"].setValidators([Validators.required]);
      } else {
        this.tuitionForm.controls["tuitionCourseLoad"].clearValidators();
        this.loadExpenses();
      }
      this.tuitionForm.controls["tuitionCourseLoad"].updateValueAndValidity();
    });
  }

  public selectCourseLoad(){
    this.selectedCourseLoadNumber = this.tuitionCourseLoad?.value;
    this.loadExpenses();
  }

  public selectHousing() {
    this.clearMealPlanInformation();
    this.clearHousingOption();
    this.selectedHousing = this.livingHousing?.value;
    this.expenseService.getSelectedHousingOptions(this.selectedHousing, this.selectedStudies).subscribe((housingOptions: Array<Expense>) => {
      this.housingOptions = housingOptions;
      if (this.housingOptions.length == 1) {
        this.selectedHousingOption = this.housingOptions[0];
        this.housingValue = this.addCommasToNumberTwoDecimal(String(this.selectedHousingOption.cost));
        this.livingForm.controls["livingHousingOption"].clearValidators();
      } else {
        this.livingForm.controls["livingHousingOption"].setValidators([Validators.required]);
      }

      if (this.selectedHousing == "USask Residence") {
        this.canPickMealPlanOption = false;
        this.mealPlanValue = "0.00";
        this.foodValue = "0.00";
        this.clearMealPlan();
        this.livingForm.controls["livingMealPlan"].clearValidators();

      } else {
        this.canPickMealPlanOption = true;
        this.livingForm.controls["livingMealPlan"].setValidators([Validators.required]);

      }
      this.resetIncidentialsDefaultValue();

      this.livingForm.controls["livingHousingOption"].updateValueAndValidity();
      this.livingForm.controls["livingMealPlan"].updateValueAndValidity();
      this.onSubmit();

    });
  }

  public selectHousingOption() {
    this.selectedHousingOption = this.livingHousingOption?.value;
    this.housingMealPlanInformation = this.selectedHousingOption.information;
    this.housingValue = this.addCommasToNumberTwoDecimal(String(this.selectedHousingOption.cost));

    this.onSubmit();
  }

  public selectHasLivingMealPlan() {
    this.selectedHasMealPlan = this.livingMealPlan?.value;
    if (this.selectedHasMealPlan == "Yes") {
      this.livingForm.controls["livingMealPlanOption"].setValidators([Validators.required]);
      this.foodValue = undefined;
    } else if (this.selectedHasMealPlan == "No") {
      this.livingForm.controls["livingMealPlanOption"].clearValidators();
      this.clearMealPlanOption();
      this.selectedMealPlanOption = this.noMealPlan;
      this.mealPlanValue = undefined;
      this.resetFoodDefaultValue();
    }
    this.onSubmit();
    this.livingForm.controls["livingMealPlanOption"].updateValueAndValidity();
  }

  public selectMealPlanOption() {
    this.selectedMealPlan = this.livingMealPlanOption?.value;
    this.expenseService.getMealPlanOption(this.selectedMealPlan, this.selectedStudies).subscribe((mealPlan: Expense) => {
      this.selectedMealPlanOption = mealPlan;
      this.mealPlanValue = this.addCommasToNumberTwoDecimal(String(mealPlan.cost));
      this.onSubmit();
    });
  }

  public selectTransportation() {
    this.selectedTransportation = this.livingTransportation?.value;
    this.expenseService.getTransportationOption(this.selectedTransportation, this.selectedStudies).subscribe((transportation: Expense) => {
      this.selectedTransportationOption = transportation;
      this.transportationValue = this.addCommasToNumberTwoDecimal(String(transportation.cost));
      this.onSubmit();
    });
  }

  public get tuitionResidency() {
    return this.tuitionForm?.get('tuitionResidency');
  }

  public get tuitionStudies() {
    return this.tuitionForm?.get('tuitionStudies');
  }

  public get tuitionCollege() {
    return this.tuitionForm?.get("tuitionCollege");
  }

  public get tuitionProgram() {
    return this.tuitionForm?.get("tuitionProgram");
  }

  public get tuitionCourseLoad() {
    return this.tuitionForm?.get("tuitionCourseLoad");
  }

  public get livingHousing() {
    return this.livingForm?.get("livingHousing");
  }

  public get livingHousingOption() {
    return this.livingForm?.get("livingHousingOption");
  }

  public get livingMealPlan() {
    return this.livingForm?.get("livingMealPlan");
  }

  public get livingMealPlanOption() {
    return this.livingForm?.get("livingMealPlanOption");
  }

  public get hasMealPlan() {
    return this.selectedHasMealPlan == "Yes";
  }

  public get livingTransportation() {
    return this.livingForm?.get("livingTransportation");
  }

  public get savingsGeneral() {
    return this.savingsForm?.get("savingsGeneral");
  }

  public get savingsWork() {
    return this.savingsForm?.get("savingsWork");
  }

  public get savingsStudentLoan() {
    return this.savingsForm?.get("savingsStudentLoan");
  }

  public get savingsAwardsAssistance() {
    return this.savingsForm?.get("savingsAwardsAssistance");
  }

  public get savingsFinancialSupportFamily() {
    return this.savingsForm?.get("savingsFinancialSupportFamily");
  }

  public get savingsOther() {
    return this.savingsForm?.get("savingsOther");
  }

  public get housingMealPlanInformationIsNotEmpty() {
    return this.housingMealPlanInformation != null && this.housingMealPlanInformation != "" && this.housingMealPlanInformation != undefined;
  }

  public get canEnterStudentLoans() {
    return this.tuitionResidency?.valid && this.tuitionResidency?.value != 'International';
  }

  public get isInternationalStudent() {
    return this.tuitionResidency?.valid && this.tuitionResidency?.value == 'International';
  }

  public get canEditMealPlanValue() {
    return ((this.livingMealPlanOption?.valid && this.hasMealPlan) || (this.selectedHousing == "USask Residence")) ?? false;
  }

  public get canEditTransportationValue() {
    return (this.livingTransportation?.valid && this.livingTransportation?.value == 'Transit') ?? false;
  }

  public get canEditHousingValue() {
    return (this.livingHousingOption?.valid && this.selectedHousing == "USask Residence") ?? false;
  }

  public get canCoverFirstYear() {
    return this.grandTotal >= 0;
  }

  private resetFoodDefaultValue(){
    this.expenseService.getDefaultFoodCost().subscribe((foodCost) => {
      this.foodValue = this.addCommasToNumberTwoDecimal(String(foodCost.cost) ?? "0.00");
      this.ref.detectChanges();
      this.onSubmit();
    });
  }

  private resetIncidentialsDefaultValue(){
      this.expenseService.getDefaultIncidentalsCost().subscribe((incidentalsCost) => {
        this.incidentalsValue = this.addCommasToNumberTwoDecimal(String(incidentalsCost.cost) ?? "0.00");
        this.ref.detectChanges();
        this.onSubmit();
    });
  }

  private clearCourseLoad(){
    this.selectedCourseLoadNumber = undefined;
    this.tuitionCourseLoad?.reset();
  }

  private clearCollege() {
    this.tuitionCollege?.reset();
    this.selectedCollege = "";
  }

  private clearProgram() {
    this.tuitionProgram?.reset();
    this.selectedProgramName = "";
    this.selectedProgram = <Program>{};
  }

  private clearHousing() {
    this.livingHousing?.reset();
    this.selectedHousing = "";
    this.livingHousingOption?.reset();
    this.selectedHousingOption = <Expense>{};
  }

  private clearIncidentals() {
    this.incidentalsValue = undefined;
  }

  private clearFood(){
    this.foodValue = undefined;
  }

  private clearHousingOption() {
    this.livingHousingOption?.reset();
    this.selectedHousingOption = <Expense>{};
  }

  private clearMealPlanInformation() {
    this.housingMealPlanInformation = "";
  }

  private clearMealPlan() {
    this.livingMealPlan?.reset();
    this.livingMealPlanOption?.reset();
    this.selectedMealPlan = "";
    this.selectedHasMealPlan = "";
    this.selectedMealPlanOption = <Expense>{};
    this.canPickMealPlanOption = false;
  }

  private clearMealPlanOption(){
    this.livingMealPlanOption?.reset();
    this.selectedMealPlanOption = <Expense>{};
  }

  private clearTransportation() {
    this.livingTransportation?.reset();
    this.selectedTransportation = "";
    this.selectedTransportationOption = <Expense>{};
  }

  private clearExpenseValues() {
    this.mealPlanValue = "";
    this.housingValue = "";
    this.transportationValue = "";
  }

  public formatSavingsGeneral() {
    this.savingsForm.controls["savingsGeneral"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsGeneral"].value));
    this.onSubmit();
  }

  public formatSavingsWork() {
    this.savingsForm.controls["savingsWork"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsWork"].value));
    this.onSubmit();
  }

  public formatSavingsStudentLoan() {
    this.savingsForm.controls["savingsStudentLoan"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsStudentLoan"].value));
    this.onSubmit();
  }

  public formatSavingsAwardsAssistance() {
    this.savingsForm.controls["savingsAwardsAssistance"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsAwardsAssistance"].value));
    this.onSubmit();
  }

  public formatSavingsFinancialSupportFamily() {
    this.savingsForm.controls["savingsFinancialSupportFamily"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsFinancialSupportFamily"].value));
    this.onSubmit();
  }

  public formatSavingsOther() {
    this.savingsForm.controls["savingsOther"].patchValue(this.addCommasToNumber(this.savingsForm.controls["savingsOther"].value));
    this.onSubmit();
  }

  public formatMealPlanValue() {
    this.mealPlanValue = this.addCommasToNumberTwoDecimal(this.mealPlanValue ?? "0.00");
    this.onSubmit();
  }

  public formatHousingValue() {
    this.housingValue = this.addCommasToNumberTwoDecimal(this.housingValue ?? "0.00");
    this.onSubmit();
  }

  public formatFoodValue() {
    this.foodValue = this.addCommasToNumberTwoDecimal(this.foodValue ?? "0.00");
    this.onSubmit();
  }

  public formatIncidentalsValue() {
    this.incidentalsValue = this.addCommasToNumberTwoDecimal(this.incidentalsValue ?? "0.00");
    this.onSubmit();
  }

  public formatTransportationValue() {
    this.transportationValue = this.addCommasToNumberTwoDecimal(this.transportationValue ?? "0.00");
    this.onSubmit();
  }

  private addCommasToNumber(toConvert: string): string{
    let value = this.convertStringToNumber(toConvert) ?? 0;
    let converted = Intl.NumberFormat('en-US',  { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true }).format(value);
    return converted;
  }

  private addCommasToNumberTwoDecimal(toConvert: string): string{
    let value = this.convertStringToNumber(toConvert) ?? 0;
    let converted = Intl.NumberFormat('en-US',  { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }).format(value);
    return converted;
  }

  public preventE(event: KeyboardEvent) {
    if (event.key === "e" || event.key === "E") {
      event.preventDefault();
    }
  }

  public handlePaste(event: ClipboardEvent) {
    var clipboardData, pastedData;

    clipboardData = event.clipboardData || (<any>window).clipboardData;
    pastedData = clipboardData.getData('Text').toUpperCase();

    if (pastedData.indexOf('E') > -1) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  public onSubmit() {
    this.showResults = true;
    this.chartLoading = true;
    this.graphData = [];
    this.totalTuition = 0;
    this.totalSavings = 0;
    this.totalLiving = 0;
    this.totalExpenses = 0;
    this.totalFees = 0;
    this.grandTotal = 0;
    this.allExpenses = [];
    this.totalSavingsArray = [];
    this.totalLivingArray = [];
    this.totalTuitionArray = [];
    this.totalFeesArray = [];
    this.processTotals();

    this.processGraph();
  }

  private processTuitionArray() {
    if (this.selectedResidency == "In Province") {
      if ((this.selectedProgram.costDomestic ?? 0) > 0) {

        this.totalTuitionArray.push({
          name: "Tuition - Domestic - In Province",
          value: this.calculateTermByCourseLoad(this.selectedProgram.costDomestic ?? 0)
        });
      }
    } else if (this.selectedResidency == "Out of Province") {
      if ((this.selectedProgram.costOutOfProvince ?? 0) > 0) {
        this.totalTuitionArray.push({
          name: "Tuition - Domestic - Out of Province",
          value: this.calculateTermByCourseLoad(this.selectedProgram.costOutOfProvince ?? 0)
        });
      }
    } else if (this.selectedResidency == "International") {
      if ((this.selectedProgram.costInternational ?? 0) > 0) {

        this.totalTuitionArray.push({
          name: "Tuition - International",
          value: this.calculateTermByCourseLoad(this.selectedProgram.costInternational ?? 0) 
        });
      }
    }
    if ((this.selectedProgram.firstYearCost ?? 0) > 0) {
      this.totalTuitionArray.push({
        name: "First Year Cost",
        value: this.selectedProgram.firstYearCost ?? 0
      });
    }
    if((this.selectedProgram.bookSupplyCost ?? 0) > 0){
      this.totalTuitionArray.push({
        name: "Books and Supplies",
        value: this.calculateTermByCourseLoad(this.selectedProgram.bookSupplyCost ?? 0)
      });
    }
  }

  private calculateTermByCourseLoad(cost:number):number{
    let calculatedTotal = 0;
    let numberOfTermsInACourseYear = 2;
    let centPlaces = 2;

    let courseMultiplier:number = ((this.selectedCourseLoadNumber ?? this.maxTuitionCourses) * numberOfTermsInACourseYear)/(this.fullCourseCountTotal);
    calculatedTotal = cost * courseMultiplier;
    
    return Number(calculatedTotal.toFixed(centPlaces));
  }

  private processFeesArray() {
    this.selectedFees.forEach((fees) => {
      this.totalFeesArray.push({
        name: fees.expense ?? "",
        value: fees.cost ?? 0
      })
    });
  }

  private processSavingsArray() {
    this.totalSavingsArray.push({
      name: "Personal savings",
      value: Number(this.convertStringToNumber(this.savingsGeneral?.value)) ?? 0
    });
    this.totalSavingsArray.push({
      name: "Employment income",
      value: Number(this.convertStringToNumber(this.savingsWork?.value)) ?? 0
    });
    if (this.selectedResidency != "International") {
      this.totalSavingsArray.push(
        {
          name: "Student loans",
          value: Number(this.convertStringToNumber(this.savingsStudentLoan?.value)) ?? 0
        });
    }
    this.totalSavingsArray.push({
      name: "Awards or financial Assistance",
      value: Number(this.convertStringToNumber(this.savingsAwardsAssistance?.value)) ?? 0
    });
    this.totalSavingsArray.push({
      name: "Family financial Support",
      value: Number(this.convertStringToNumber(this.savingsFinancialSupportFamily?.value)) ?? 0
    });
    this.totalSavingsArray.push({
      name: "Other sources of funding",
      value: Number(this.convertStringToNumber(this.savingsOther?.value)) ?? 0
    });
  }

  private processTotals() {

    this.processFeesArray();
    this.processTuitionArray();
    this.processSavingsArray();

    this.totalSavingsArray.forEach((savings) => {
      this.totalSavings += savings.value;
    });

    this.totalTuitionArray.forEach((tuition) => {
      this.totalTuition += tuition.value;
    });

    this.totalFeesArray.forEach((fee) => {
      this.totalFees += fee.value;
    });


    this.totalLiving += Number(this.convertStringToNumber(this.mealPlanValue?? "0.00"));
    this.totalLiving += Number(this.convertStringToNumber(this.transportationValue ?? "0.00"));
    this.totalLiving += Number(this.convertStringToNumber(this.housingValue ?? "0.00"));
    this.totalLiving += Number(this.convertStringToNumber(this.foodValue ?? "0.00"));
    this.totalLiving += Number(this.convertStringToNumber(this.incidentalsValue ?? "0.00"));

    this.totalExpenses = this.totalTuition + this.totalLiving + this.totalFees;
    this.grandTotal = this.totalSavings - this.totalExpenses;
  }

  private processGraph() {

    this.graphData = [
      {
        data: [
          {
            value: this.totalExpenses,
            itemStyle: { color: '#000000' },
            type: 'bar'
          },
          {
            value: this.totalSavings,
            itemStyle: { color: '#0b6a41' },
            type: 'bar'
          },
        ],

      },
    ]
    this.mergeChartData = {
      series: this.graphData
    }
    this.chartLoading = false;
  }

  public goBack() {
    this.showResults = false;
  }

  public printDiv() {
    window.print();
  }

  public formatMoney(toConvert: number) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(toConvert);
  }

  open(content: TemplateRef<any>) {
    this.modalReference = this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
    this.modalReference.result.then(
      (result) => {
        this.closeResult = `Closed with: ${result}`;
      },
      (reason) => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      },
    );
  }

  private convertStringToNumber(currency: string): number{
    if(currency != null || currency != undefined){
      currency =  currency.replace(/[^0-9.]+/g,"").replace(/^(\d*\.?)|(\d*)\.?/g, "$1$2")
      let converted = Number(currency);
      if(Number.isNaN(converted) || !Number.isFinite(converted)){
        return 0;
      }
      return converted;
    } else {
      return 0;
    }
  }

  private getDismissReason(reason: any): string {
    switch (reason) {
      case ModalDismissReasons.ESC:
        return 'by pressing ESC';
      case ModalDismissReasons.BACKDROP_CLICK:
        return 'by clicking on a backdrop';
      default:
        return `with: ${reason}`;
    }
  }

  private orderFeeKeys(){
    this.tableSelectedFees = new Map(this.assessmentTypeOrder.filter(key => this.tableSelectedFees.has(key))
      .map(key => [key, this.tableSelectedFees.get(key) ?? []]));
  }

  private isFullTime():boolean{
     return Boolean((this.selectedCourseLoadNumber ?? this.maxTuitionCourses) > this.partTimeCourseMax)
  }
}
